// The opposing squadron: a heavy cruiser and two destroyers, voxel-built at
// 1:1 scale, steaming a zigzag approach. Once inside gun range they shoot
// back — their solutions improve the longer they track you, and their
// shells arrive as real ballistic rounds through the same shell simulator.

import * as THREE from "three";
import { V, KTS } from "./const.js";
import { VoxelGrid, gridToMeshes } from "./voxel.js";
import { solveElevation } from "./guns.js";

const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));

function buildEnemyModel(len, beam, kind) {
  const g = new VoxelGrid(len + 2, 30, beam + 4);
  const cz = Math.floor((beam + 4) / 2);
  const hb0 = beam / 2;
  for (let x = 1; x <= len; x++) {
    const f = x / len;
    let hb = hb0;
    if (f > 0.72) hb = hb0 * (1 - Math.pow((f - 0.72) / 0.28, 1.6));
    if (f < 0.15) hb = hb0 * (0.55 + 3 * f);
    hb = Math.max(0.5, hb);
    for (let y = 0; y <= 8; y++) {
      const hbY = y < 3 ? hb * 0.7 : hb;
      const z0 = Math.ceil(cz - hbY + 0.5), z1 = Math.floor(cz + hbY - 0.5);
      if (z1 >= z0) g.fill(x, x, y, y, z0, z1, y === 8 ? V.DECKSTEEL : y <= 4 ? V.BOTTOM : V.HULL);
    }
  }
  const mid = Math.floor(len / 2);
  // superstructure block + bridge
  g.fill(mid - 12, mid + 6, 9, 12, cz - 3, cz + 3, V.SUPER);
  g.fill(mid + 2, mid + 8, 9, 15, cz - 2, cz + 2, V.SUPER);
  // funnels
  g.fill(mid - 8, mid - 5, 13, 18, cz - 2, cz + 2, V.FUNNEL);
  if (kind === "cruiser") g.fill(mid - 16, mid - 13, 13, 17, cz - 2, cz + 2, V.FUNNEL);
  // turrets fore & aft
  const turret = (tx, w) => {
    g.fill(tx - 2, tx + 2, 9, 10, cz - w, cz + w, V.TURRET);
    g.fill(tx + 2, tx + 6, 9, 9, cz - 1, cz + 1, V.BARREL);
  };
  turret(Math.floor(len * 0.78), 2);
  turret(Math.floor(len * 0.24), 2);
  if (kind === "cruiser") { turret(Math.floor(len * 0.86), 2); turret(Math.floor(len * 0.14), 2); }
  // mast
  g.fill(mid + 6, mid + 6, 16, 22, cz, cz, V.SUPER);
  const mesh = gridToMeshes(g);
  // darken toward silhouette — that's all you ever see of them at 25 km
  mesh.traverse((o) => {
    const col = o.isMesh && o.geometry.getAttribute("color");
    if (col) { for (let i = 0; i < col.array.length; i++) col.array[i] *= 0.62; }
  });
  mesh.position.set(-len / 2, -3, -cz - 0.5); // waterline at hull y3
  return mesh;
}

const NAMES = {
  cruiser: ["TAKAO", "MYOKO", "MOGAMI"],
  dd: ["KAGERO", "YUKIKAZE", "SHIMAKAZE", "AKIZUKI"],
};

export class Enemy {
  constructor(G, kind, x, z, heading, name) {
    this.G = G;
    this.kind = kind;
    this.name = name;
    this.len = kind === "cruiser" ? 200 : 118;
    this.beam = kind === "cruiser" ? 19 : 11;
    this.pos = new THREE.Vector2(x, z);
    this.heading = heading;
    this.speed = (kind === "cruiser" ? 18 : 24) * KTS;
    this.turnRate = 0;
    this.legTimer = 20 + Math.random() * 60;
    this.hp = kind === "cruiser" ? 10 : 5;
    this.maxHp = this.hp;
    this.sunk = false;
    this.sinkT = 0;
    this.fires = 0;
    this.gunTimer = 25 + Math.random() * 30;
    this.trackTime = 0;    // how long they've been shooting at us (accuracy ramp)
    this.group = new THREE.Group();
    this.group.add(buildEnemyModel(this.len, this.beam, kind));
    G.scene.add(this.group);
    this.smokeAcc = 0;
    this.torpsAway = false;
    // night: unlit voxel silhouettes fade to near-black; a star shell's
    // light (enemy brightness is faked through material.color, since the
    // voxel meshes are deliberately unlit) brings them back up
    this._mats = [];
    this.group.traverse((o) => { if (o.isMesh && o.material?.color) this._mats.push(o.material); });
    this._lit = G.night ? 0.14 : 1;
    if (G.night) for (const m of this._mats) m.color.setScalar(this._lit);
  }

  update(dt) {
    const G = this.G;
    if (this.sunk) {
      this.sinkT += dt;
      this.group.position.y = -Math.min(14, this.sinkT * 0.55);
      this.group.rotation.x = Math.min(0.2, this.sinkT * 0.01);
      if (this.sinkT < 25 && Math.random() < dt * 8) {
        G.worldFx.spawn(new THREE.Vector3(this.pos.x, 3, this.pos.y), {
          count: 3, speed: 3, life: 4, size: 5, jitter: this.len / 4,
          colors: [0x23272c, 0x3a3f45, 0x14181c], gravity: 1.4, drag: 0.6, grow: 2,
        });
      }
      return;
    }

    // zigzag legs, biased so the squadron keeps closing on us
    this.legTimer -= dt;
    if (this.legTimer <= 0) {
      this.legTimer = 50 + Math.random() * 70;
      const p0 = this.G.shipWorldPos();
      const towardUs = Math.atan2(p0.x - this.pos.x, p0.z - this.pos.y);
      this.targetHeading = towardUs + (Math.random() - 0.5) * 1.1;
    }
    if (this.targetHeading !== undefined) {
      const d = wrap(this.targetHeading - this.heading);
      this.turnRate = Math.abs(d) > 0.015 ? Math.sign(d) * 0.02 : 0;
      this.heading += this.turnRate * dt;
      if (Math.abs(d) < 0.02) this.turnRate = 0;
    }
    this.pos.x += Math.sin(this.heading) * this.speed * dt;
    this.pos.y += Math.cos(this.heading) * this.speed * dt;

    this.group.position.set(this.pos.x, 0, this.pos.y);
    this.group.rotation.y = this.heading - Math.PI / 2;

    // coal-black funnel smoke — how you spot a squadron over the horizon
    this.funnelAcc = (this.funnelAcc || 0) + dt;
    if (this.funnelAcc > 0.3) {
      this.funnelAcc = 0;
      G.worldFx.spawn(new THREE.Vector3(
        this.pos.x - Math.sin(this.heading) * 6, 16, this.pos.y - Math.cos(this.heading) * 6), {
        count: 1, speed: 1.5, life: 9, size: 7, jitter: 3,
        dir: new THREE.Vector3(-Math.sin(this.heading) * 0.8, 1, -Math.cos(this.heading) * 0.8),
        colors: [0x2e3134, 0x3c3f43, 0x26282b], gravity: 1.5, drag: 0.4, grow: 4,
      });
    }

    // burning?
    if (this.fires > 0) {
      this.smokeAcc += dt;
      if (this.smokeAcc > 0.12) {
        this.smokeAcc = 0;
        G.worldFx.spawn(new THREE.Vector3(
          this.pos.x + (Math.random() - 0.5) * this.len * 0.4, 10, this.pos.y + (Math.random() - 0.5) * 6), {
          count: 1, speed: 2, life: 6, size: 4 * this.fires, jitter: 2,
          colors: [0x1c1c1c, 0x3a342c, 0xff8a3c], gravity: 2.2, drag: 0.7, grow: 3,
        });
      }
      this.hp -= dt * 0.01 * this.fires; // burn down
      if (this.hp <= 0) this.sink();
    }

    // night illumination state: silhouette brightens while a flare's up
    if (G.night) {
      const want = G.illum?.isLit(this) ? 0.92 : 0.14;
      if (Math.abs(want - this._lit) > 0.01) {
        this._lit += (want - this._lit) * Math.min(1, dt * 2.2);
        for (const m of this._mats) m.color.setScalar(this._lit);
      }
    }

    // gunnery
    const p = G.shipWorldPos();
    const range = Math.hypot(this.pos.x - p.x, this.pos.y - p.z);

    // destroyers press in for a torpedo attack: one spread of Long Lance
    // at the predicted point of aim, then haul clear
    if (this.kind === "dd" && !this.torpsAway && G.hostile && G.damage.afloat
        && range < 6800 && this.trackTime > 4 && G.torps) {
      this.torpsAway = true;
      const s = G.shipState;
      const runT = range / 34;
      const ax = p.x + Math.sin(s.heading) * s.speed * runT;
      const az = p.z + Math.cos(s.heading) * s.speed * runT;
      G.torps.launch(this.pos.x, this.pos.y, Math.atan2(ax - this.pos.x, az - this.pos.y), 4);
      this.targetHeading = Math.atan2(this.pos.x - p.x, this.pos.y - p.z); // turn away
      this.legTimer = 60;
    }

    if (G.hostile && range < 23000 && G.damage.afloat) {
      this.trackTime += dt;
      this.gunTimer -= dt;
      if (this.gunTimer <= 0) {
        this.gunTimer = this.kind === "cruiser" ? 32 + Math.random() * 18 : 22 + Math.random() * 14;
        this.fireAt(p, range);
      }
    } else {
      this.trackTime = Math.max(0, this.trackTime - dt);
    }
  }

  fireAt(p, range) {
    const G = this.G;
    const guns = this.kind === "cruiser" ? 4 : 2;
    // their accuracy: starts wild, converges with track time; DDs are worse
    const base = this.kind === "cruiser" ? 620 : 880;
    const err = base * Math.max(0.22, 1 - this.trackTime / 200);
    // aim at our predicted position
    const sol = solveElevation(range);
    const s = G.shipState;
    const aimX = p.x + Math.sin(s.heading) * s.speed * sol.tof + randn2() * err;
    const aimZ = p.z + Math.cos(s.heading) * s.speed * sol.tof + randn2() * err;
    for (let i = 0; i < guns; i++) {
      const tx = aimX + randn2() * (this.kind === "cruiser" ? 140 : 210);
      const tz = aimZ + randn2() * (this.kind === "cruiser" ? 140 : 210);
      const dx = tx - this.pos.x, dz = tz - this.pos.y;
      const r = Math.hypot(dx, dz), b = Math.atan2(dx, dz);
      const es = solveElevation(r);
      const dir = new THREE.Vector3(Math.sin(b) * Math.cos(es.elev), Math.sin(es.elev), Math.cos(b) * Math.cos(es.elev));
      G.guns.spawnShell(new THREE.Vector3(this.pos.x, 10, this.pos.y), dir.multiplyScalar(800), { own: false, shooter: this });
    }
    // muzzle flash on the horizon + delayed thunder
    G.worldFx.spawn(new THREE.Vector3(this.pos.x, 12, this.pos.y), {
      count: 10, speed: 12, life: 0.5, size: 14, jitter: 8,
      colors: [0xffd890, 0xffb050], drag: 2 });
    const delay = Math.min(20, range / 340);
    setTimeout(() => G.audio.distantGuns(), delay * 1000);
    G.hud.log(`${this.name} FIRING — SPLASHES IN ${Math.round(solveElevation(range).tof)} S`, "bad");
    setTimeout(() => G.audio.shellWhistle(), Math.max(0, solveElevation(range).tof - 3) * 1000);
  }

  // Our shell landed at `at` — did it strike this ship?
  tryHit(at) {
    if (this.sunk) return null;
    const dx = at.x - this.pos.x, dz = at.z - this.pos.y;
    // rotate into ship frame: fwd along heading
    const fwd = dx * Math.sin(this.heading) + dz * Math.cos(this.heading);
    const side = dx * Math.cos(this.heading) - dz * Math.sin(this.heading);
    if (Math.abs(fwd) < this.len / 2 + 4 && Math.abs(side) < this.beam / 2 + 3) {
      this.hp -= 1;
      this.fires = Math.min(3, this.fires + (Math.random() < 0.6 ? 1 : 0));
      const spot = new THREE.Vector3(this.pos.x + (Math.random() - 0.5) * this.len * 0.5, 8, this.pos.y);
      this.G.worldFx.spawn(spot, { count: 30, speed: 20, life: 0.8, size: 6, jitter: 3,
        colors: [0xffcf5c, 0xff8a3c, 0x2c2c2c], drag: 1.5, grow: 2 });
      let text = "AMIDSHIPS";
      if (fwd > this.len * 0.2) text = "FORWARD";
      else if (fwd < -this.len * 0.2) text = "AFT";
      if (this.hp <= 0) { this.sink(); text += " — SHE'S GOING DOWN"; }
      else if (this.fires >= 2) text += ", LARGE FIRES";
      return { text };
    }
    return null;
  }

  sink() {
    if (this.sunk) return;
    this.sunk = true;
    this.sinkT = 0;
    this.speed = 0;
    this.G.hud.log(`${this.name} IS SINKING`, "ok");
  }
}

// Night raid: a light cruiser leading three destroyers, already inside
// 19 km and coming in hard for a torpedo attack — the Solomons play.
export function spawnNightRaid(G) {
  const baseB = 0.5 + Math.random() * 0.8;
  const R = 19000;
  const cx = Math.sin(baseB) * R, cz = Math.cos(baseB) * R;
  const heading = baseB + Math.PI + 0.3;
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  const mk = (kind, dx, dz, name) => new Enemy(G, kind, cx + dx, cz + dz, heading, name);
  return [
    mk("cruiser", 0, 0, "IJN " + pick(NAMES.cruiser)),
    mk("dd", 1500, 900, "IJN " + NAMES.dd[0]),
    mk("dd", -1400, 700, "IJN " + NAMES.dd[1]),
    mk("dd", 500, 2200, "IJN " + NAMES.dd[2]),
  ];
}

export function spawnSquadron(G) {
  // approach from the north-east, ~26 km out, in loose column
  const baseB = 0.7 + Math.random() * 0.6; // bearing from us
  const R = 26000;
  const cx = Math.sin(baseB) * R, cz = Math.cos(baseB) * R;
  const heading = baseB + Math.PI + 0.5; // roughly closing
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  return [
    new Enemy(G, "cruiser", cx, cz, heading, "IJN " + pick(NAMES.cruiser)),
    new Enemy(G, "dd", cx + Math.sin(heading + Math.PI) * 1200 + 600, cz + Math.cos(heading + Math.PI) * 1200, heading, "IJN " + pick(NAMES.dd.slice(0, 2))),
    new Enemy(G, "dd", cx + Math.sin(heading + Math.PI) * 2400 - 600, cz + Math.cos(heading + Math.PI) * 2400, heading, "IJN " + pick(NAMES.dd.slice(2))),
  ];
}

function randn2() {
  return (Math.random() + Math.random() + Math.random() + Math.random() - 2) / 1.2;
}
